import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faPen,
} from '@fortawesome/sharp-solid-svg-icons';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Mark from '@/components/icons/Mark';
import Typography from '@/components/typography';
import { AcresFeatureFlags } from '@/constants/constants';
import { CUSTOMIZE_LAND_PLAT_MAP_URL } from '@/constants/urls';
import { CustomizationAttachment } from '@/endpoints/itasca/land/saved-selections/customize/getSavedSelectionFeatures';
import { getSavedSelectionFeatureAttachments } from '@/endpoints/itasca/land/saved-selections/customizeOld/getSavedSelectionFeatureAttachments';
import { customizeIconMap } from '@/features/customizeMyLand/config';
import { CustomizeSettingsDetailsMap } from '@/features/customizeMyLand/customizeSettingsBox/customizeSettingsDetails';
import {
  CustomizeFeature,
  useCustomizeLandContext,
} from '@/features/customizeMyLand/provider';
import { ToolMapper, ToolMenu } from '@/features/toolbar/constants';
import { useToolbarContext } from '@/features/toolbar/provider';
import { useGetFeatureFlag } from '@/hooks/useGetFeatureFlag';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useModalContext } from '@/providers/ModalProvider';

import { CustomizationReadonlyDialogBoxDetailsSection } from './detailsSection';

type Props = {
  className?: string;
};

export const PlatmapCustomizationReadonlyDialogBox: React.FC<Props> = ({
  className = '',
}) => {
  const {
    activeFeature,
    setActiveFeature,
    customizations: { allCustomizations },
  } = useCustomizeLandContext();
  const { setSelectedTool, setActiveToolbarFeature } = useToolbarContext();
  const isToolbarActive = useGetFeatureFlag(
    AcresFeatureFlags.TEMP_TOOL_TOP_BAR
  );

  const focusFeature = useCallback(
    (
      feature: React.SetStateAction<
        CustomizeFeature | Feature<Geometry, GeoJsonProperties> | null
      >
    ) => {
      setActiveFeature(feature as CustomizeFeature);
    },
    [setActiveFeature]
  );

  const router = useRouter();

  const onEditClick = useCallback(() => {
    if (!isToolbarActive) {
      router.push(CUSTOMIZE_LAND_PLAT_MAP_URL);
    } else {
      setSelectedTool(ToolMapper[ToolMenu.Select]);
    }
  }, [isToolbarActive, router, setSelectedTool]);

  const { setModalContent } = useModalContext();

  const [attachments, setAttachments] = useState<CustomizationAttachment[]>([]);

  const { activeFile, activeFileId } = useFileManagementContext();

  const getAttachmentsForFeature = useCallback(async () => {
    if (!activeFile || !activeFeature?.id) return;

    if (!Number(activeFeature.id)) return;

    const { res } = await getSavedSelectionFeatureAttachments(
      activeFile.id.toString(),
      activeFeature.id.toString()
    );

    setAttachments(
      res?.data?.filter(Boolean).map((attachment) => ({
        file_name: attachment.file_name ?? '',
        id: attachment.id ?? '',
        name: attachment.name ?? '',
        url: attachment.url ?? '',
      })) ?? []
    );
  }, [activeFeature, activeFile]);

  useEffect(() => {
    getAttachmentsForFeature();
  }, [getAttachmentsForFeature]);

  //* * Set the active customization to the next one in the customizations array */
  const nextCustomization = useCallback(() => {
    if (!activeFeature) {
      return;
    }
    const currentIndex = allCustomizations.indexOf(activeFeature, 0);
    if (currentIndex >= allCustomizations.length - 1) {
      focusFeature(allCustomizations[0] as CustomizeFeature);
      return;
    }
    focusFeature(allCustomizations[currentIndex + 1] as CustomizeFeature);
  }, [activeFeature, allCustomizations, focusFeature]);

  /** Set the active customization to the previous one in the customizations array */
  const prevCustomization = useCallback(() => {
    if (!activeFeature || !allCustomizations) {
      return;
    }

    const currentIndex = allCustomizations.indexOf(activeFeature, 0);
    if (currentIndex <= 0) {
      focusFeature(allCustomizations[allCustomizations.length - 1]);
      return;
    }

    focusFeature(allCustomizations[currentIndex - 1]);
  }, [activeFeature, allCustomizations, focusFeature]);

  const details = activeFeature
    ? CustomizeSettingsDetailsMap[activeFeature.geometry.type]?.(activeFeature)
    : '';

  useEffect(() => {
    if (isToolbarActive) return;
    setActiveFeature(null);
  }, [activeFileId, isToolbarActive, setActiveFeature]);

  if (!activeFeature) return null;

  return (
    <div
      className={twMerge(
        'flex w-[260px] flex-col bg-white sm:w-[300px]',
        'dark:bg-gray-100/[.85]',
        className
      )}
    >
      <div className="p-2">
        <div className="mb-5 flex items-center justify-between">
          {onEditClick ? (
            <IconButton
              className="flex w-min flex-row items-center gap-2 text-green-50 dark:bg-gray-90 dark:text-white dark:hover:bg-gray-100"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.GHOST}
              onClick={onEditClick}
            >
              <FontAwesomeIcon
                className="min-h-[12px] min-w-[12px]"
                icon={faPen}
              />
              <Typography>Edit</Typography>
            </IconButton>
          ) : (
            ''
          )}
          <IconButton
            className="dark:bg-gray-90 dark:text-white dark:hover:bg-gray-100"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={() => {
              setActiveFeature(null);
              setActiveToolbarFeature(null);
            }}
          >
            <Mark className="h-3 w-3" />
          </IconButton>
        </div>

        <CustomizationReadonlyDialogBoxDetailsSection
          attachments={attachments}
          description={activeFeature?.properties?.description}
          details={details}
          feature={activeFeature}
          iconNode={customizeIconMap[activeFeature?.properties?.id]}
          setModalContent={setModalContent}
          title={activeFeature?.properties?.name}
        />
      </div>

      {/* Button section */}
      {allCustomizations?.length > 1 && (
        <div
          className={twMerge(
            'flex justify-between  px-4 py-3',
            'border-t-1 border-t-gray-20 bg-gray-10',
            'dark:border-t-0 dark:bg-gray-100'
          )}
        >
          <Button
            className="flex gap-x-2"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={prevCustomization}
          >
            <FontAwesomeIcon icon={faArrowLeft} onClick={prevCustomization} />
            Back
          </Button>
          <Button
            className="flex gap-x-2"
            size={ButtonSize.SMALL}
            variant={ButtonVariant.GHOST}
            onClick={nextCustomization}
          >
            Next
            <FontAwesomeIcon icon={faArrowRight} onClick={nextCustomization} />
          </Button>
        </div>
      )}
    </div>
  );
};
